/**
 * parseFrontmatter.ts — minimal YAML frontmatter extractor for
 * .claude/agents and .claude/skills docs.
 *
 * No external library — keeps the bundle small and avoids a new dep just for
 * this surface. Parses only the fields the PWA metadata panels actually use.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AgentMetadata {
  name: string;
  description: string;
  tools: string[];
  model: string;
  effort: string;
  /** Skill slugs listed under alkio.skills (e.g. "execute-step"). */
  skills: string[];
  /** Contract IDs listed under alkio.contracts[*].id. */
  contractIds: string[];
}

export interface SkillMetadata {
  name: string;
  description: string;
  /** Execution grain — e.g. "single-use" or "shared". */
  grain: string;
  /** Steward persona — e.g. "executor", "orchestrator". */
  steward: string;
  /** Extracted trigger conditions from "Triggers on X, Y" in description. */
  triggerConditions?: string[];
  /** First non-heading paragraph of the skill body, up to 300 chars. */
  procedureSummary?: string;
}

// ---------------------------------------------------------------------------
// Core parsing helpers (exported for unit testing)
// ---------------------------------------------------------------------------

/**
 * Extract raw YAML text between the first `---` delimiter pair.
 * Returns null if the document does not start with a frontmatter block.
 */
export function extractFrontmatterText(raw: string): string | null {
  if (!raw.startsWith("---")) return null;
  const firstNewline = raw.indexOf("\n");
  if (firstNewline === -1) return null;
  const closeIdx = raw.indexOf("\n---", firstNewline);
  if (closeIdx === -1) return null;
  return raw.slice(firstNewline + 1, closeIdx);
}

/**
 * Parse simple top-level `key: value` lines (zero-indent).
 * Skips blank lines, comments, and lines that are section headers (no value).
 */
export function parseTopLevelFields(fmText: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of fmText.split("\n")) {
    const m = line.match(/^([a-zA-Z]\w*): (.+)$/);
    if (m) result[m[1]] = m[2].trim();
  }
  return result;
}

/**
 * Extract simple string items from a two-level nested YAML path:
 *
 *   parentKey:
 *     listKey:
 *       - item1
 *       - item2
 *
 * Items that begin with `key: value` (i.e. are YAML object entries) are
 * excluded — use `extractNestedObjectField` for those.
 */
export function extractNestedList(
  fmText: string,
  parentKey: string,
  listKey: string,
): string[] {
  const lines = fmText.split("\n");
  const items: string[] = [];
  let inParent = false;
  let inList = false;

  for (const line of lines) {
    if (line === `${parentKey}:`) {
      inParent = true;
      inList = false;
      continue;
    }
    if (inParent && line === `  ${listKey}:`) {
      inList = true;
      continue;
    }
    if (inList) {
      if (line.startsWith("    - ")) {
        const item = line.slice(6).trim();
        // Skip object-style items (they start with "key: value")
        if (!item.includes(": ")) items.push(item);
        continue;
      }
      // Exit list on any non-blank line that isn't deeper indented
      if (line !== "" && !line.startsWith("    ")) inList = false;
    }
    // Exit parent section on any non-blank line at zero indent
    if (inParent && line !== "" && !line.startsWith(" ")) inParent = false;
  }
  return items;
}

/**
 * Extract the value of a specific `fieldKey:` from objects in a nested list:
 *
 *   parentKey:
 *     listKey:
 *       - fieldKey: value1
 *         otherKey: ...
 *       - otherKey: ...
 *         fieldKey: value2
 *
 * Handles the field appearing either on the `- ` line itself or on a
 * continuation line indented by 6 spaces.
 */
export function extractNestedObjectField(
  fmText: string,
  parentKey: string,
  listKey: string,
  fieldKey: string,
): string[] {
  const lines = fmText.split("\n");
  const values: string[] = [];
  let inParent = false;
  let inList = false;

  for (const line of lines) {
    if (line === `${parentKey}:`) {
      inParent = true;
      inList = false;
      continue;
    }
    if (inParent && line === `  ${listKey}:`) {
      inList = true;
      continue;
    }
    if (inList) {
      // List item that opens with "- fieldKey: value"
      if (line.startsWith("    - ")) {
        const item = line.slice(6).trim();
        const m = item.match(new RegExp(`^${fieldKey}: (.+)$`));
        if (m) values.push(m[1].trim());
        continue;
      }
      // Continuation line "      fieldKey: value"
      if (line.startsWith("      ")) {
        const m = line.trim().match(new RegExp(`^${fieldKey}: (.+)$`));
        if (m) values.push(m[1].trim());
        continue;
      }
      if (line !== "" && !line.startsWith("    ")) inList = false;
    }
    if (inParent && line !== "" && !line.startsWith(" ")) inParent = false;
  }
  return values;
}

// ---------------------------------------------------------------------------
// High-level parsers
// ---------------------------------------------------------------------------

/**
 * Parse structured metadata from a .claude/agents/*.md document.
 * Returns null if the document has no frontmatter or is missing a `name:`.
 */
export function parseAgentMetadata(raw: string): AgentMetadata | null {
  const fmText = extractFrontmatterText(raw);
  if (!fmText) return null;
  const fields = parseTopLevelFields(fmText);
  if (!fields.name) return null;

  // tools: must be comma-separated on one line ("Bash, Read") — YAML list syntax not supported
  const tools = fields.tools
    ? fields.tools
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  return {
    name: fields.name,
    description: fields.description ?? "",
    tools,
    model: fields.model ?? "",
    effort: fields.effort ?? "",
    skills: extractNestedList(fmText, "alkio", "skills"),
    contractIds: extractNestedObjectField(fmText, "alkio", "contracts", "id"),
  };
}

/**
 * Parse structured metadata from a .claude/skills skill document.
 * Returns null if the document has no frontmatter or is missing a `name:`.
 */
export function parseSkillMetadata(raw: string): SkillMetadata | null {
  const fmText = extractFrontmatterText(raw);
  if (!fmText) return null;
  const fields = parseTopLevelFields(fmText);
  if (!fields.name) return null;

  const description = fields.description ?? "";
  const triggerMatch = description.match(/Triggers on ([^.]+)/i);
  const triggerConditions = triggerMatch
    ? triggerMatch[1]
        .split(/[,;]/)
        .map((t) => t.trim())
        .filter(Boolean)
    : undefined;

  // Extract first non-heading paragraph from the body (after the closing ---).
  let procedureSummary: string | undefined;
  if (raw.startsWith("---")) {
    const firstNl = raw.indexOf("\n");
    if (firstNl !== -1) {
      const closeIdx = raw.indexOf("\n---", firstNl);
      if (closeIdx !== -1) {
        const body = raw.slice(closeIdx + 4);
        for (const para of body.split(/\n\n+/)) {
          const trimmed = para.trim();
          if (!trimmed || trimmed.startsWith("#")) continue;
          procedureSummary = trimmed.slice(0, 300);
          break;
        }
      }
    }
  }

  return {
    name: fields.name,
    description,
    grain: fields.grain ?? "",
    steward: fields.steward ?? "",
    ...(triggerConditions !== undefined ? { triggerConditions } : {}),
    ...(procedureSummary !== undefined ? { procedureSummary } : {}),
  };
}
